import { Checkbox } from "@mui/material";
import { Menu } from "components";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "components/atoms/BasicTable";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { getClientICP } from "store/slices/icp.slice";
import { handleRevealContactByID } from "store/slices/insights";
import { useTable } from "utils/hooks/useTable";
import { PulseRow } from "../row";
import { bodyContainer } from "../../style";
import Icon from "components/atoms/Icon/Icon";
import constants from "utils/constants";
import { Empty } from "components/atoms/Empty";
import { emptyMessages } from "utils/message";
import PulseListSkeleton from "components/atoms/layout/PulseListSkeleton/PulseListSkeleton";

const PulseBody = () => {
  const {
    data: leads,
    isLoading,
    count,
  } = useAppSelector((state) => state.insights.leads);
  const { data: user } = useAppSelector((state) => state.user);
  const { exportOpen, filterOpen } = useAppSelector((state) => state.ui);
  const {
    selectedRows,
    unselectedIds,
    isAllSelected,
    handleSelectRow,
    handleSelectAll,
    handleSelectAllInPage,
    handleUnselectRow,
    handleDeselectAll,
  } = useTable({ totalItems: count });
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const open = Boolean(anchor);
  const { container } = bodyContainer({
    isExport: exportOpen,
    sidebarOpen: filterOpen,
  });
  const dispatch = useAppDispatch();
  const [searchParams, _] = useSearchParams();
  const { data: ICPData } = useAppSelector((state) => state.icp);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      const timeout = setTimeout(() => setShowContent(true), 200);
      return () => clearTimeout(timeout);
    } else {
      setShowContent(false);
    }
  }, [isLoading]);

  useEffect(() => {
    dispatch(getClientICP());
  }, [dispatch]);

  const thresholdBehavior = ICPData?.thresholdMinimumBehavior || 0;
  const thresholdIcp = ICPData?.thresholdMinimumIcp || 0;
  const currentParams = searchParams.toString();

  if (!user.clientId) {
    return (
      <div
        data-testid="register-tracking-pixel"
        className="text-white-500 text-center flex flex-col gap-3 justify-center items-center p-4 py-12 sm:py-24 w-2/3 m-auto"
      >
        <Icon name="sad" size={70} />
        <h1 className="text-xl mt-4">
          You have no{" "}
          <span className="capitalize">{constants.PIXEL_KEYWORD}</span>
        </h1>
        <p className="text-gray-400 text-base">
          Your current subscription status restricts access to{" "}
          <span className="capitalize">{constants.PIXEL_KEYWORD}</span>.
        </p>
        <p className="text-gray-400 mb-5 text-base">
          Please contact our team by the email{" "}
          <a
            className="link"
            href={`mailto:${constants.COMPANY_1_EMAIL_SUPPORT}`}
          >
            {constants.COMPANY_1_EMAIL_SUPPORT}
          </a>
          . We are here to help you enjoy the full range of services we offer.
        </p>
        <p className="text-gray-400 text-base">
          De-anonymize the visitors on your website and receive data within
          hours after placing our tracking pixel.
        </p>
        <p className="pt-3 text-white-500 text-base font-bold">{`Copy and paste this code into the <HEAD> of every webpage you want to track:`}</p>
      </div>
    );
  }
  if (!leads) {
    return (
      <div data-testid="empty-leads" className={container()}>
        <div className="flex flex-col items-center justify-center gap-4 w-2/3 h-full m-auto">
          <div className="bg-primary-500 flex justify-center items-center rounded-lg text-white-500 w-[58px] h-[52px]">
            <Icon size={37} name="code" />
          </div>
          <h1 className="font-bold text-xl">
            Start by setting up a tracking pixel
          </h1>
          <div className="space-y-3">
            <p className="text-gray-400 text-base">
              Turn anonymous clicks and page views into tangible leads by
              identifying individuals and companies behind the web visits.
            </p>
            <p className="text-gray-400 text-base">
              While some traffic will ultimately stay anonymous, you will find
              that much of your traffic can be deanonymized to give you valuable
              insights that translate into strategic marketing decisions.
            </p>
            <p className="text-gray-400 text-base">
              De-anonymize the visitors on your website and receive data within
              hours after placing our tracking pixel.
            </p>
            {/* <p className="pt-3 text-white-500 text-base font-bold">{clientTrackingPixel.data?.id ? `Copy and paste this code into the <HEAD> of every webpage you want to track:` : 'Go to pixel section to configure your pixel'}</p> */}
          </div>
        </div>
      </div>
    );
  }
  if (leads && leads.length === 0 && !isLoading) {
    return <Empty message={emptyMessages.matchingResult} />;
  }
  return (
    <>
      {isLoading && (
        <div className="opacity-100 transition-opacity duration-300">
          <PulseListSkeleton removeHeader />
        </div>
      )}

      {!isLoading && (
        <div
          className={`${container()} ${
            showContent ? "opacity-100" : "opacity-0"
          } transition-opacity duration-300`}
        >
          <Table>
            <TableHeader>
              <TableRow className="!max-h-[32px]">
                <TableCell
                  isHeader
                  classNameComponent="p-0 max-w-[20px]"
                  className="w-1 max-w-[20px] !p-0"
                >
                  <Checkbox
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isAllSelected) {
                        handleDeselectAll();
                        return;
                      }
                      setAnchor(event.currentTarget);
                    }}
                    checked={isAllSelected}
                    sx={{
                      color: "var(--color-gray-600)",
                      "&.Mui-checked": { color: "var(--color-primary-500)" },
                      "&:hover": { backgroundColor: "transparent" },
                    }}
                  />
                </TableCell>
                <TableCell
                  isHeader
                  className="w-1 max-w-[20px] !p-0 !font-semibold !text-white-500"
                >
                  Score
                </TableCell>
                <TableCell
                  isHeader
                  className="w-1 max-w-[20px] !p-0 !font-semibold !text-white-500"
                >
                  Behavior
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 text-left w-[160px] !font-semibold !text-white-500"
                >
                  Lead
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 text-left !font-semibold !text-white-500"
                >
                  Company
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 w-4 !font-semibold !text-white-500"
                >
                  Visits
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 w-[110px] truncate whitespace-nowrap !font-semibold !text-white-500"
                >
                  Page Views
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 w-[20px] !font-semibold !text-white-500"
                >
                  Duration
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 w-[120px] truncate whitespace-nowrap !font-semibold !text-white-500"
                >
                  Last Seen
                </TableCell>
                <TableCell
                  isHeader
                  className="!px-4 !p-0 w-[140px] !font-semibold !text-white-500"
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads?.map((row) => (
                <PulseRow
                  clientId={ICPData?.clientId || ""}
                  currentParams={currentParams}
                  thresholdIcp={thresholdIcp}
                  thresholdBehavior={thresholdBehavior}
                  selectedRows={selectedRows}
                  unselectedIds={unselectedIds}
                  isAllSelected={isAllSelected}
                  handleSelectRow={handleSelectRow}
                  handleUnselectRow={handleUnselectRow}
                  {...row}
                  handleRevealContactByID={() =>
                    dispatch(
                      handleRevealContactByID({ leadId: row.id })
                    ).unwrap()
                  }
                />
              ))}
            </TableBody>
          </Table>
          <Menu
            items={[
              {
                title: "Select this page",
                onClick: () =>
                  handleSelectAllInPage(leads.map((item) => item.id)),
              },
              {
                title: `Select all people (${
                  count < constants.LIMIT_ENRICHMENT_ATTEMPT_PER_TASK
                    ? count
                    : constants.LIMIT_ENRICHMENT_ATTEMPT_PER_TASK.toLocaleString()
                })`,
                onClick: () => handleSelectAll(),
              },
            ]}
            isOpen={open}
            anchor={anchor}
            setAnchor={setAnchor}
            position="left"
          />
        </div>
      )}
    </>
  );
};
export default PulseBody;
